import React, { useMemo } from 'react';
import { Alert, Space, Typography, Tag } from 'antd';
import { useRegulatoryAlerts } from 'providers/RegulatoryAlerts';
import useTenantFlags from 'hooks/useTenantFlags';

const { Text } = Typography;

// Banner de alertas regulatorias para el tab General de la ficha de cliente.
// Lee GET /clientes/:idCliente/alerts y resume:
//  - criticals (rojo)
//  - warnings (naranja)
//  - infos  (azul, colapsadas — solo total)
// Si no hay alertas, no renderiza nada.

const TIPO_LABELS = {
  certificacion_vence: 'Certificación por vencer',
  certificacion_vencida: 'Certificación vencida',
  mantencion_mensual_faltante: 'Mantención faltante',
  datos_incompletos: 'Datos incompletos',
};

const SEVERITY_COLORS = {
  critical: 'red',
  warning: 'orange',
  info: 'blue',
};

function RegulatoryAlertsBanner({ idCliente }) {
  const { minvuEnabled } = useTenantFlags();
  const { alerts, loading } = useRegulatoryAlerts(idCliente);

  const { critical, warning, info } = useMemo(() => {
    const crit = [];
    const warn = [];
    const inf = [];
    for (const a of alerts ?? []) {
      if (a.severidad === 'critical') crit.push(a);
      else if (a.severidad === 'warning') warn.push(a);
      else inf.push(a);
    }
    return { critical: crit, warning: warn, info: inf };
  }, [alerts]);

  if (!minvuEnabled) return null;
  if (loading) return null;
  if (!alerts || alerts.length === 0) return null;

  const headerType = critical.length > 0 ? 'error' : warning.length > 0 ? 'warning' : 'info';
  const total = alerts.length;

  return (
    <Alert
      type={headerType}
      showIcon
      banner
      style={{ borderRadius: 8 }}
      message={
        <Space size="small" wrap>
          <Text strong>
            {total} alerta{total > 1 ? 's' : ''} regulatoria{total > 1 ? 's' : ''} activa
            {total > 1 ? 's' : ''}
          </Text>
          {critical.length > 0 && (
            <Tag color={SEVERITY_COLORS.critical}>{critical.length} críticas</Tag>
          )}
          {warning.length > 0 && (
            <Tag color={SEVERITY_COLORS.warning}>{warning.length} atención</Tag>
          )}
          {info.length > 0 && <Tag color={SEVERITY_COLORS.info}>{info.length} informativas</Tag>}
        </Space>
      }
      description={
        <Space direction="vertical" size={2} style={{ marginTop: 4 }}>
          {critical.slice(0, 3).map((a) => (
            <Text key={a.id} strong>
              {TIPO_LABELS[a.tipo] ?? a.tipo}: {a.mensaje}
            </Text>
          ))}
          {warning.slice(0, Math.max(0, 5 - critical.length)).map((a) => (
            <Text key={a.id}>
              {TIPO_LABELS[a.tipo] ?? a.tipo}: {a.mensaje}
            </Text>
          ))}
          {total > 5 && <Text type="secondary">... y {total - 5} más en el tab Equipos</Text>}
        </Space>
      }
    />
  );
}

export default RegulatoryAlertsBanner;
